import React from 'react';
import { get, isUndefined, find, cloneDeep, map, isEmpty, noop, isEqual, size, split, trim, toString, parseInt } from 'lodash';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';

// React view component to render
import { memberCheckoutPaymentOptions as MemberCheckoutPaymentOptionsView } from 'ui/views/widgets/memberCheckoutPaymentOptions';
import { MemberCheckoutPaymentOptionsService } from 'services/memberCheckoutPaymentOptions';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS, SIZES } from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as confirmOverlay from 'utils/confirmOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberCheckoutPaymentOptions } from 'ui/containers/MemberCheckoutPaymentOptions';
 *
 * // render
 * <MemberCheckoutPaymentOptions propName='propValue'/>
 */
class _MemberCheckoutPaymentOptions extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind
        this.handlePaymentOptionSelection = this.handlePaymentOptionSelection.bind( this );
        this.handlePaymentDefaultSelection = this.handlePaymentDefaultSelection.bind( this );
        this.handleInactivatePaymentInfo = this.handleInactivatePaymentInfo.bind( this );
        this.handleEditPaymentInfo = this.handleEditPaymentInfo.bind( this );
        this.handleActivatePaymentInfo = this.handleActivatePaymentInfo.bind( this );
		this.submitActivatePaymentInfo = this.submitActivatePaymentInfo.bind( this );
        this.handleHsaFsaActionToggle = this.handleHsaFsaActionToggle.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // Member Detail instance
        this.memberCheckoutPaymentOptionsService = new MemberCheckoutPaymentOptionsService();

        // start member detail data fetch
        this.fetchComponentData();
    }

    /**
     * fetch data
     */
    async fetchComponentData() {
        log.debug( 'MemberSavedAddress.fetchComponentData()' );

        // cancel ongoing request
        this.memberCheckoutPaymentOptionsService.cancel();

        // fetch component data
        const { status, data } = await this.memberCheckoutPaymentOptionsService.fetchComponentData( {
            url: this.props.widget.url,
            data: {
                ...this.props.requestPayload
            }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data
            }, () => {

                // get the payment options
                const paymentOptions = get( data, 'paymentOptions', [] );

                const isPaymentRequired = this.props.isPaymentRequired;

                //get selected payment
                const paymentOption = find( paymentOptions, { selected: true } );

                if( ! isUndefined( paymentOptions ) ) {
    
                    if( isPaymentRequired ) {
                        this.props.updateCheckoutSelection( { type: 'paymentOption', item: paymentOption } );
                    } else {

                        // if payment is not required, loop through payment options and unselect the payment
                        const componentData = cloneDeep( this.state.componentData );
                        const updatedList = map( componentData.paymentOptions, item => {
                            item.selected = false;
                            return item;
                        } );
                        componentData.paymentOptions = updatedList;
                
                        this.setState( {
                            componentData
                        }, () => {
                            this.props.updateCheckoutSelection( { type: 'paymentOption', item: {} } );
                        } );
                    }
                }


            } );
        }
    }

    /**
     * update selection
     */
    handlePaymentOptionSelection( { card } ) {
        log.debug( 'MemberCheckoutPaymentOptions.handlePaymentOptionSelection()' );

        const componentData = cloneDeep( this.state.componentData );
        const updatedList = map( componentData.paymentOptions, item => {
            item.selected = item.id === card.id ? true: false;
            return item;
        } );
        componentData.paymentOptions = updatedList;

        this.setState( {
            componentData
        }, () => {
            this.props.updateCheckoutSelection( { type: 'paymentOption', item: card } );

        } );
    }

    /**
     * update default payment selection
     */
    async handlePaymentDefaultSelection( { card } ) {
        log.debug( 'MemberCheckoutPaymentOptions.handlePaymentDefaultSelection()' );

        // cancel ongoing request
        this.memberCheckoutPaymentOptionsService.cancel();

        //show preloader
        pageLevelPreloader.show();

        const url = find( this.state.componentData.modifyActions, { name: 'setDefault' } )[ 'url' ];

        // set the address default
        const { status, data } = await this.memberCheckoutPaymentOptionsService.setDefaultPaymentInfo( {
            url,
            data: {
                context: get( this.props, 'requestPayload.context', {} ),
                data: { id: card.id }
            },
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update address list
            const componentData = cloneDeep( this.state.componentData );
            const updatedList = map( componentData.paymentOptions, item => {
                item.flags.isDefault = card.id === item.id ? true: false;
                return item;
            } );
            componentData.paymentOptions = updatedList;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {
                successOverlay.open( {
                    message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                } );
            } );


        } else {
            
            // show error dialog
            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * update edit payment info
     */
    async handleEditPaymentInfo( { card, month, year } = {}, callback = noop ) {
        log.debug( 'MemberCheckoutPaymentOptions.handleEditPaymentInfo()' );

        const action = find( this.state.componentData.modifyActions, { name: 'edit' } );
 
        // cancel ongoing request
        this.memberCheckoutPaymentOptionsService.cancel();

        //hide preloader
        pageLevelPreloader.show();

        // set the address default
        const { status, data } = await this.memberCheckoutPaymentOptionsService.submitEditedPaymentInfo( {
            url: action.url,
            data: {
                context: get( this.props, 'requestPayload.context', {} ),
                data: {
                    id: card.id,
                    month: month,
                    year: year,
                }
            },
        } );

        //hide preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // update expiry date
            const componentData = cloneDeep( this.state.componentData );
            const cardItem = find( componentData.paymentOptions, item => {
                return card.id === item.id;
            } );
            cardItem.expDate = `${ month }/${ year }`;

            this.setState( {
                componentData,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, callback );

        } else {

            if( ! isEmpty( get( data, 'description' ) ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
            } else {
                errorOverlay.open();
            }
        }
        
    }

    /**
	 * initiate activate payment info
	 */
	async handleActivatePaymentInfo( { card } ) {
		log.debug( 'MemberCheckoutPaymentOptions.handleActivatePaymentInfo()' );

		let [ expiryMonth, expiryYear ] = split( card.expDate, '/' );
		expiryMonth = parseInt( trim( expiryMonth ) );
		expiryYear = parseInt( trim( expiryYear ) );

		const currentMonth = parseInt( moment().format( 'MM' ) );
		const currentYear = parseInt( moment().format( 'YY' ) );

		if ( expiryYear < currentYear || ( expiryYear === currentYear && expiryMonth < currentMonth ) ) {
			overlay.open( {
				settings: {
					modalType: OVERLAY_VIEWS.PAYMENT_CARD_ACTIVATION,
                    showCloseButton: false,
                    ignoreOutsideClick: false
				},
				data: {
					onActivateActionClick: this.submitActivatePaymentInfo,
					onSubmitEditedDate: this.handleEditPaymentInfo,
					onCancelClick: overlay.close,
                    onHsaFsaActionToggle: this.handleHsaFsaActionToggle,
					actions: this.state.componentData.modifyActions,
					requestPayload: this.props.requestPayload,
					card
				}
			} );
		} else {
			const action = find( this.state.componentData.modifyActions, { name: 'activate' } );
			this.submitActivatePaymentInfo( { url: action.url, id: card.id, month: toString( expiryMonth ), year: toString( expiryYear ) } );
		}
	}

	/**
	 * submit activate payment info
	 */
	async submitActivatePaymentInfo( { url, id, month, year } ) {
		log.debug( 'MemberCheckoutPaymentOptions.submitActivatePaymentInfo()' );

		this.memberCheckoutPaymentOptionsService.cancel();

		overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.memberCheckoutPaymentOptionsService.submitActivatePaymentInfoRequest( {
			url,
			data: {
				context: { memberId: this.props.requestPayload.context.memberId },
				data: {
					id,
					month,
					year
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isActivated = true;

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			} );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}

    /**
     * update delete payment info
     */
     async handleInactivatePaymentInfo( { card } ) {
		log.debug( 'MemberCheckoutPaymentOptions.handleInactivatePaymentInfo()' );

		const action = find( this.state.componentData.modifyActions, {
			name: 'inactivate',
		} );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( action.confirmation.message ),
			iconName: 'close',
			confirmViewConfig: {
				type: 'paymentInfo',
				card: card
			}
		} );

		if ( shouldProceed ) {

			// cancel ongoing request
			this.memberCheckoutPaymentOptionsService.cancel();

			//hide preloader
			pageLevelPreloader.show();

			// set the address default
			const { status, data } = await this.memberCheckoutPaymentOptionsService.deletePaymentInfo( {
				url: action.url,
				data: {
					context: { memberId: this.props.requestPayload.context.memberId },
					data: { id: card.id }
				}
			} );

			//hide preloader
			pageLevelPreloader.hide();

			if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

				// update payment options
				const componentData = cloneDeep( this.state.componentData );

				const updatedList = map( componentData.paymentOptions, ( item ) => {
					if ( item.id === card.id ) {
						const updatedItem = cloneDeep( item );
						updatedItem.selected = false;
						updatedItem.flags.isActivated = false;
						updatedItem.flags.isDefault = false;

						return updatedItem;
					}

					return item;
				} );
				
				componentData.paymentOptions = updatedList;

				this.setState( {
					componentData,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				}, () => {
                    if ( this.props.selectedPaymentOptionId === card.id ) {
                        this.props.updateCheckoutSelection( { type: 'paymentOption', item: {} } );
                    }
                } );
			} else {
				if( ! isEmpty( get( data, 'description' ) ) ) {
					errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
				} else {
					errorOverlay.open();
				}
			}
		}
	}

    /**
     * @desc When location changes, update state and re-render the map
     */
    UNSAFE_componentWillReceiveProps( newProps ) {
        log.debug( 'GoogleMap.componentWillReceiveProps()' );

        if( ! isEqual( newProps.isPaymentRequired, this.props.isPaymentRequired ) ) {

            const componentData = cloneDeep( this.state.componentData );

            if( ! newProps.isPaymentRequired && 0 !== size(  get( componentData, 'paymentOptions', [] ) ) ) {
                
                const updatedList = map( componentData.paymentOptions, item => {
                    item.selected = false;
                    return item;
                } );
                componentData.paymentOptions = updatedList;
        
                this.setState( {
                    componentData
                }, () => {
                    this.props.updateCheckoutSelection( { type: 'paymentOption', item: {} } );
                } );
            }
            
        }
    }

	/**
	 * submit hsaFsa toggle info
	 */
     async handleHsaFsaActionToggle( { url, id, flags }, callback = noop  ) {
		log.debug( 'MemberCheckoutPaymentOptions.handleHsaFsaActionToggle()' );

		this.memberCheckoutPaymentOptionsService.cancel();

		// overlay.close();

		pageLevelPreloader.show();

		const { status, data } = await this.memberCheckoutPaymentOptionsService.submitToggleHsaFsaPaymentInfoRequest( {
			url,
			data: {
				context: { memberId: this.props.requestPayload.context.memberId },
				data: {
					id,
                    isHsaFsa: ! get( flags, 'isHsaFsa', false )
				}
			}
		} );

		pageLevelPreloader.hide();

		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			const componentData = cloneDeep( this.state.componentData );

			const updatedList = map( componentData.paymentOptions, ( item ) => {
				if ( item.id === id ) {
					const updatedItem = cloneDeep( item );
					updatedItem.flags.isHsaFsa = ! get( flags, 'isHsaFsa', false );

					return updatedItem;
				}

				return item;
			} );
			
			componentData.paymentOptions = updatedList;

			this.setState( {
				componentData,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
			}, callback );
		} else {
			if( ! isEmpty( get( data, 'description' ) ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
			} else {
				errorOverlay.open();
			}
		}
	}

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
    */
    componentWillUnmount() {
        this.memberCheckoutPaymentOptionsService.cancel(); // cancel ongoing requests
    }


    /**
     * @desc Render a React view component
     */
    render() {

        // const memberId = get( this.props.requestPayload, 'context.memberId', '' );

        // construct render redirection path for add payment
        const addPaymentRedirectPath = queryString.stringifyUrl( {
            url: this.props.location.pathname,
            query: {
                step: this.props.stepIndex + 1, // active tab
                origin: this.props.origin
            }
        } );

        return (
            <MemberCheckoutPaymentOptionsView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} )}
                requestPayload={ this.props.requestPayload }

                isPaymentRequired={ this.props.isPaymentRequired }
                onPaymentOptionSelection={ this.handlePaymentOptionSelection }
                onPaymentDefaultSelection={ this.handlePaymentDefaultSelection }
                onPaymemntInfoActivate={ this.handleActivatePaymentInfo }
                onPaymemntInfoInactivate = { this.handleInactivatePaymentInfo }
                onPaymemntInfoEdit = { this.handleEditPaymentInfo }
                onHsaFsaActionToggle = { this.handleHsaFsaActionToggle }
                addPaymentRedirectPath={ addPaymentRedirectPath }
            />
        );
    }
}

// set display name
_MemberCheckoutPaymentOptions.displayName = '_MemberCheckoutPaymentOptions';

// set default props
_MemberCheckoutPaymentOptions.defaultProps = {};

// prop types
_MemberCheckoutPaymentOptions.propTypes = {};

/*********************************************/

// with router functionality
export const MemberCheckoutPaymentOptions = withRouter( _MemberCheckoutPaymentOptions );
